import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { callAPI } from "../helper/apiUtils";
import { BASE_URL } from "../helper/constants";
import LanguageContext from "../helper/LanguageContext";
import CompanyDocumentViewerModal from "./CompanyDocumentViewerModal";
import { HiDownload, HiOutlineEye } from "react-icons/hi";
import "./CompanyDocumentsFeedSection.css";

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Compact Firmendokumente on the internal feed.
 * Collapsed = label + count + arrow only (no first filename).
 * Expand in place to open the list. Sidebar page stays separate.
 */
export default function CompanyDocumentsFeedSection({ companyId }) {
  const { strings } = useContext(LanguageContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await callAPI(
        `/company/${companyId}/document`,
        {},
        "GET"
      );
      if (data?.status && Array.isArray(data.data)) {
        setDocuments(data.data);
        try {
          sessionStorage.setItem("kjuup_intern_docs_company_id", companyId);
        } catch (_) { /* ignore */ }
      } else {
        setDocuments([]);
      }
    } catch (e) {
      console.error(e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    setExpanded(false);
    load();
  }, [load]);

  const downloadDoc = async (item) => {
    if (!item) return;
    setDownloadingId(item.id);
    try {
      const response = await axios({
        url: BASE_URL + item.fileUrl,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        item.attachedFileName || item.title || "document"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingId(null);
    }
  };

  if (!companyId) {
    return null;
  }

  const title = strings.company_documents || "Firmendokumente";
  const viewLabel = strings.preview || strings.view || "Ansehen";
  const downloadLabel = strings.download_selected || "Download";
  const count = documents.length;

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const renderDocRow = (item) => (
    <div
      key={item.id}
      className="company-documents-feed-item company-documents-feed-section__item"
    >
      <div className="company-documents-feed-item-text">
        <div className="company-documents-feed-item-title">
          {item.title || item.attachedFileName}
        </div>
        <div className="company-documents-feed-item-meta">
          {formatBytes(item.fileSize)}
          {item.createdAt
            ? ` · ${new Date(item.createdAt).toLocaleDateString()}`
            : ""}
        </div>
      </div>
      <span
        className="company-documents-feed-actions"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setViewDoc(item)}
          title={viewLabel}
          aria-label={viewLabel}
          className="kj-btn kj-btn--icon kj-btn--sm kj-btn--internal"
        >
          <HiOutlineEye size={18} />
        </button>
        <button
          type="button"
          onClick={() => downloadDoc(item)}
          disabled={downloadingId === item.id}
          title={downloadLabel}
          aria-label={downloadLabel}
          className="kj-btn kj-btn--icon kj-btn--sm kj-btn--internal"
        >
          <HiDownload size={16} />
        </button>
      </span>
    </div>
  );

  return (
    <>
      <div
        className="company-documents-feed-section company-documents-feed-compact company-documents-feed-section__root"
      >
        <div
          className={`company-documents-feed-header company-documents-feed-section__header${expanded ? " company-documents-feed-section__header--open" : ""}`}
          onClick={toggleExpanded}
          role="button"
          aria-expanded={expanded}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
        >
          <span
            className="material-symbols-outlined company-documents-feed-section__icon"
          >
            folder_shared
          </span>
          <span
            className="company-documents-feed-header-title company-documents-feed-section__title"
          >
            {title}
          </span>
          {!loading && count > 0 && (
            <span className="company-documents-feed-count-badge">
              {count}
            </span>
          )}
          <span
            className={`material-symbols-outlined company-documents-feed-section__icon company-documents-feed-section__chevron${expanded ? " company-documents-feed-section__chevron--open" : ""}`}
          >
            chevron_right
          </span>
        </div>

        {expanded && (
          <div className="company-documents-feed-section__list">
            {count === 0 && !loading ? (
              <div
                className="company-documents-feed-preview-empty company-documents-feed-section__empty"
              >
                {strings.company_documents_empty || "Keine Dokumente vorhanden."}
              </div>
            ) : (
              documents.map((item) => renderDocRow(item))
            )}
          </div>
        )}
        {expanded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
            className="kj-btn kj-btn--ghost kj-btn--sm kj-btn--block kj-btn--internal"
          >
            <span>{strings.show_less || "Weniger anzeigen"}</span>
            <span className="material-symbols-outlined company-documents-feed-section__less-icon">
              expand_less
            </span>
          </button>
        )}
      </div>

      {viewDoc && (
        <CompanyDocumentViewerModal
          fileUrl={viewDoc.fileUrl}
          fileName={viewDoc.attachedFileName || viewDoc.title || "document"}
          onClose={() => setViewDoc(null)}
          onDownload={() => downloadDoc(viewDoc)}
          downloadLabel={downloadLabel}
        />
      )}
    </>
  );
}
